import React from 'react';
import { FileCode, Check, Copy, Info } from 'lucide-react';

interface BadgeCodeSnippetsProps {
  activeCodeTab: 'script' | 'iframe' | 'svg' | 'url';
  embedCode: string;
  copied: boolean;
  onCodeTabChange: (tab: 'script' | 'iframe' | 'svg' | 'url') => void;
  onCopy: () => void;
}

export const BadgeCodeSnippets: React.FC<BadgeCodeSnippetsProps> = ({
  activeCodeTab,
  embedCode,
  copied,
  onCodeTabChange,
  onCopy,
}) => {
  return (
    <div className="space-y-4">
      {/* Snippet Code Tabs & Copy */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-xs font-bold text-slate-800 uppercase tracking-wide flex items-center gap-1.5">
            <FileCode className="w-3.5 h-3.5 text-indigo-600" />
            Salin Potongan Kode Sematan
          </label>

          {/* Code Tab Switcher */}
          <div className="flex bg-slate-200/80 p-0.5 rounded-lg text-xs font-medium">
            <button
              type="button"
              onClick={() => onCodeTabChange('script')}
              className={`px-2.5 py-1 rounded-md transition ${
                activeCodeTab === 'script' ? 'bg-white text-indigo-700 font-bold shadow-xs' : 'text-slate-600'
              }`}
            >
              HTML & JS
            </button>
            <button
              type="button"
              onClick={() => onCodeTabChange('iframe')}
              className={`px-2.5 py-1 rounded-md transition ${
                activeCodeTab === 'iframe' ? 'bg-white text-indigo-700 font-bold shadow-xs' : 'text-slate-600'
              }`}
            >
              Iframe
            </button>
            <button
              type="button"
              onClick={() => onCodeTabChange('svg')}
              className={`px-2.5 py-1 rounded-md transition ${
                activeCodeTab === 'svg' ? 'bg-white text-indigo-700 font-bold shadow-xs' : 'text-slate-600'
              }`}
            >
              SVG / Markdown
            </button>
            <button
              type="button"
              onClick={() => onCodeTabChange('url')}
              className={`px-2.5 py-1 rounded-md transition ${
                activeCodeTab === 'url' ? 'bg-white text-indigo-700 font-bold shadow-xs' : 'text-slate-600'
              }`}
            >
              URL Tautan
            </button>
          </div>
        </div>

        {/* Code Block Container */}
        <div className="relative rounded-xl overflow-hidden bg-slate-900 border border-slate-800 shadow-md">
          <pre className="p-4 text-xs font-mono text-emerald-400 overflow-x-auto leading-relaxed max-h-40 selection:bg-emerald-900 selection:text-white">
            <code>{embedCode}</code>
          </pre>

          {/* Copy button */}
          <button
            type="button"
            onClick={onCopy}
            className="absolute top-3 right-3 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 transition shadow-sm cursor-pointer"
          >
            {copied ? (
              <>
                <Check className="w-3.5 h-3.5 text-emerald-300" />
                <span>Tersalin!</span>
              </>
            ) : (
              <>
                <Copy className="w-3.5 h-3.5" />
                <span>Salin Kode</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Quick Guide on How Publishers Embed */}
      <div className="p-3.5 bg-slate-100/80 rounded-xl border border-slate-200/80 text-xs text-slate-600 space-y-1.5">
        <div className="font-bold text-slate-800 flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5 text-indigo-600" />
          <span>Panduan Penerbit: Cara Menempelkan Widget di Web Anda</span>
        </div>
        <ul className="list-disc pl-4 space-y-1 text-[11px] leading-relaxed">
          <li>
            <strong>WordPress / CMS:</strong> Tambahkan blok <em>Custom HTML</em> pada halaman deskripsi buku Anda, lalu tempelkan kode di atas.
          </li>
          <li>
            <strong>Shopify / WooCommerce / Toko Daring:</strong> Tempelkan kode pada bagian template produk atau deskripsi buku.
          </li>
          <li>
            <strong>Website Kustom / Webflow:</strong> Cukup salin cuplikan kode <code>&lt;div class="uibn-badge" ...&gt;</code> ke dalam file template HTML situs Anda.
          </li>
        </ul>
      </div>
    </div>
  );
};
